import { useCallback, useMemo } from "react";
import { CollectionUtils } from "utilities/collection-utils";
import UserPublicationFavoriteService, {
    MoveUserPublicationFavoriteParams,
    UserPublicationFavoritePathParams,
} from "../../../services/user-publication-favorites/user-publication-favorite-service";
import { useGlobalState } from "../../../contexts/use-global-state-context";
import UserPublicationFavoriteRecord from "../../../../models/view-models/user-publication-favorite-record";
import { useSortedRecords } from "../use-sorted-records";
import PublicationRecord from "../../../../models/view-models/publication-record";
import { ToastManager } from "../../../toast/toast-manager";
import { useLocalization } from "../../use-localization";

export interface UseSortUserPublicationFavorites {
    onFavoritesReordered: (
        updatedFavorites: UserPublicationFavoriteRecord[]
    ) => Promise<void>;
    sortedFavoritePublications: PublicationRecord[];
    sortedFavorites: UserPublicationFavoriteRecord[];
}

export interface SortItemMovement {
    movingRecordId: number;
    precedingRecordId?: number;
}

export const useSortUserPublicationFavorites = (
    favorites: UserPublicationFavoriteRecord[]
): UseSortUserPublicationFavorites => {
    const { patch: moveApi } = UserPublicationFavoriteService.useMove();
    const { globalState } = useGlobalState();
    const { t } = useLocalization();
    const { records, onRecordsMoved, resetRecordSort } =
        useSortedRecords<UserPublicationFavoriteRecord>(
            favorites,
            UserPublicationFavoriteRecord
        );

    const currentUserId = useMemo(
        () => globalState.currentIdentity?.userId(),
        [globalState]
    );

    const currentUserRoleId = useMemo(
        () => globalState.currentIdentity?.getCurrentUserRole()?.id,
        [globalState]
    );

    const publications = useMemo(
        () => records.map((upf) => upf.publication),
        [records]
    );

    const makeMoveApiCall = useCallback(
        async (
            movePathParams: UserPublicationFavoritePathParams,
            move: MoveUserPublicationFavoriteParams
        ): Promise<void> => {
            try {
                const moveResponse = await moveApi(movePathParams, move);

                if (moveResponse.result?.hasErrors()) {
                    throw new Error(
                        moveResponse.result?.listErrorMessages().join(", ")
                    );
                }
            } catch (error) {
                ToastManager.error(
                    t("errors-actionResource", {
                        action: t("moving"),
                        resource: t("favorite"),
                    })
                );
                resetRecordSort();
            }
        },
        [moveApi, resetRecordSort, t]
    );

    const moveFavorites = useCallback(
        async (moves: MoveUserPublicationFavoriteParams[]): Promise<void> => {
            if (CollectionUtils.isEmpty(moves) || currentUserId == null) {
                return;
            }

            const movePathParams: UserPublicationFavoritePathParams = {
                userId: currentUserId,
            };

            moves.forEach(await makeMoveApiCall.bind(null, movePathParams));
        },
        [currentUserId, makeMoveApiCall]
    );

    const onFavoritesReordered = useCallback(
        async (updatedFavorites: UserPublicationFavoriteRecord[]) => {
            const diffs = onRecordsMoved(updatedFavorites);
            const moves = diffsToFavoriteMoves(diffs, currentUserRoleId);

            await moveFavorites(moves);
        },
        [currentUserRoleId, moveFavorites, onRecordsMoved]
    );

    return {
        onFavoritesReordered: onFavoritesReordered,
        sortedFavoritePublications: publications,
        sortedFavorites: records,
    };
};

const diffsToFavoriteMoves = (
    diffs: SortItemMovement[],
    currentUserRoleId?: number
): MoveUserPublicationFavoriteParams[] => {
    if (currentUserRoleId == null) {
        return [];
    }

    return diffs.map(diffToMove.bind(null, currentUserRoleId));
};

const diffToMove = (
    currentUserRoleId: number,
    diff: SortItemMovement
): MoveUserPublicationFavoriteParams => ({
    movingUserPublicationFavoriteId: diff.movingRecordId,
    precedingUserPublicationFavoriteId: diff.precedingRecordId,
    userRoleId: currentUserRoleId,
});
